import type { EngineInput, EngineResult, TradeEngine } from '../estimate.types';
import { calculateBathroomEstimate } from './bathroomEngine';
import { calculatePavingEstimate } from './pavingEngine';
import { calculateEstimate as calculatePaintingEstimate } from '../simpleEstimateEngine';
import { withDomainProfitLeaks } from '../profitLeakRules';

function resolveTradeEngine(input: EngineInput): TradeEngine {
  if (input.tradeEngine) {
    return input.tradeEngine;
  }

  switch (input.trade) {
    case 'malarz':
      return 'painting';
    case 'lazienka':
      return 'bathroom';
    case 'ogolnobudowlany':
      return 'general';
    default:
      return 'painting';
  }
}

function withFallbackWarning(result: EngineResult, warning: string): EngineResult {
  return {
    ...result,
    meta: {
      ...result.meta,
      calculations: {
        ...result.meta.calculations,
        calculationNote: result.meta.calculations.calculationNote
          ? `${result.meta.calculations.calculationNote} ${warning}`
          : warning,
      },
    },
  };
}

function withMetaSchedule(result: EngineResult): EngineResult {
  if (!result.scheduleEstimate) {
    return result;
  }

  return {
    ...result,
    meta: {
      ...result.meta,
      scheduleEstimate: result.scheduleEstimate,
    },
  };
}

function calculateBasicFallback(input: EngineInput, warning: string): EngineResult {
  const result = calculatePaintingEstimate(input);
  return withFallbackWarning(result, warning);
}

function normalizePavingInput(input: EngineInput): EngineInput {
  const hasWasteRemovalM3 = input.pavingWasteRemovalM3 !== undefined;
  const hasCuttingLength = input.pavingCuttingLengthMb !== undefined;
  const legacyEdgeLength = input.edgeLengthM && input.edgeLengthM > 0 ? input.edgeLengthM : undefined;

  return {
    ...input,
    edgeLengthM:
      legacyEdgeLength ??
      input.pavingEdgeLengthMb ??
      input.pavingCurbLengthMb,
    baseThicknessCm:
      input.baseThicknessCm ??
      input.pavingSubbaseThicknessCm ??
      input.pavingExcavationDepthCm,
    beddingThicknessCm: input.beddingThicknessCm ?? input.pavingBeddingThicknessCm,
    crewSize: input.crewSize ?? input.pavingCrewSize,
    requiresGeotextile: input.requiresGeotextile ?? input.pavingHasGeotextile,
    requiresSpoilRemoval: input.requiresSpoilRemoval ?? (hasWasteRemovalM3 ? (input.pavingWasteRemovalM3 ?? 0) > 0 : undefined),
    requiresCutting: input.requiresCutting ?? (hasCuttingLength ? (input.pavingCuttingLengthMb ?? 0) > 0 : undefined),
  };
}

export function calculateEstimate(input: EngineInput): EngineResult {
  const tradeEngine = resolveTradeEngine(input);
  let result: EngineResult;

  switch (tradeEngine) {
    case 'painting':
      result = calculatePaintingEstimate(input);
      break;
    case 'bathroom':
      result = calculateBathroomEstimate(input);
      break;
    case 'paving':
      result = withMetaSchedule(calculatePavingEstimate(normalizePavingInput(input)));
      break;
    case 'general':
      result = calculateBasicFallback(
        input,
        'Tryb ogolnobudowlany MVP: uzyto podstawowego fallbacku. Dodaj pozycje branzowe w kolejnym kroku.'
      );
      break;
    case 'tiling':
    case 'drywall':
    case 'exterior':
      result = calculateBasicFallback(
        input,
        `Silnik ${tradeEngine} jest przygotowany jako strategia MVP. Na razie uzyto podstawowego fallbacku.`
      );
      break;
    default:
      result = calculateBasicFallback(input, 'Nieznana branza: uzyto podstawowego fallbacku.');
  }

  return withDomainProfitLeaks(input, result);
}
